import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon';
import type { useSmilesImport } from '../../hooks/useSmilesImport';

interface SmilesImportPanelProps {
  readonly smilesImport: ReturnType<typeof useSmilesImport>;
  readonly autoFocus?: boolean;
  readonly onSuccess?: () => void;
}

export function SmilesImportPanel({ smilesImport, autoFocus = false, onSuccess }: SmilesImportPanelProps) {
  const { t } = useTranslation();
  const { smilesInput, updateSmilesInput, smilesImportState, handleSmilesImport } = smilesImport;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [pasteError, setPasteError] = useState<'noText' | 'denied' | null>(null);

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  const pasteFromClipboard = async () => {
    setPasteError(null);
    try {
      if (!navigator.clipboard?.readText) {
        setPasteError('denied');
        return;
      }
      const text = await navigator.clipboard.readText();
      if (!text || text.trim() === '') {
        setPasteError('noText');
        return;
      }
      updateSmilesInput(text.trim());
      inputRef.current?.focus();
    } catch {
      setPasteError('denied');
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPasteError(null);
    const outcome = await handleSmilesImport(event);
    if (outcome.state === 'success' && onSuccess) {
      updateSmilesInput('');
      onSuccess();
    }
  };

  return (
    <section className="smiles-import-panel" aria-labelledby="smiles-import-heading">
      <div>
        <h2 id="smiles-import-heading">{t('smilesImportPanel.heading')}</h2>
        <p>{t('smilesImportPanel.subheading')}</p>
      </div>
      <form onSubmit={(event) => void handleSubmit(event)}>
        <label htmlFor="smiles-input">{t('smilesImportPanel.label')}</label>
        <input
          ref={inputRef}
          id="smiles-input"
          type="text"
          value={smilesInput}
          placeholder={t('smilesImportPanel.placeholder')}
          spellCheck="false"
          onChange={(event) => {
            setPasteError(null);
            updateSmilesInput(event.target.value);
          }}
        />
        <button
          className="icon-button"
          type="button"
          aria-label={t('smilesImportPanel.pasteButton')}
          title={t('smilesImportPanel.pasteButton')}
          disabled={smilesImportState.state === 'loading'}
          onClick={() => void pasteFromClipboard()}
        >
          <Icon name="paste" />
        </button>
        <button
          className={smilesImportState.state === 'loading' ? '' : 'icon-button'}
          type="submit"
          aria-label={t('smilesImportPanel.importButton')}
          title={t('smilesImportPanel.importButton')}
          disabled={smilesImportState.state === 'loading' || smilesInput.trim() === ''}
        >
          {smilesImportState.state === 'loading' ? t('smilesImportPanel.importing') : <Icon name="arrowForward" />}
        </button>
      </form>
      {pasteError ? (
        <p className="import-error" role="alert">
          {t(`smilesImportPanel.pasteErrors.${pasteError}`)}
        </p>
      ) : null}
      {smilesImportState.state === 'error' ? (
        <p className="import-error" role="alert">
          {t('smilesImportPanel.importFailed', { message: smilesImportState.message })}
        </p>
      ) : null}
      {smilesImportState.state === 'success' ? (
        <p className="import-success" role="status">
          {t('smilesImportPanel.importedAs', { canonicalSmiles: smilesImportState.canonicalSmiles })}
        </p>
      ) : null}
    </section>
  );
}
